import React, { createContext, useCallback, useEffect, useState } from "react";
import API from "../api/api";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(null);
    const [username, setUsername] = useState(null);
    const [role, setRole] = useState(null);
    const [authChecked, setAuthChecked] = useState(false);
    const [loading, setLoading] = useState(false);

    const applyUser = useCallback((userData) => {
        setToken("cookie-session");
        setUsername(userData?.username || null);
        setRole(userData?.role || null);
    }, []);

    const clearUser = useCallback(() => {
        setToken(null);
        setUsername(null);
        setRole(null);
    }, []);

    const checkAuth = useCallback(async () => {
        try {
            const res = await API.get("/users/me/", {
                skipAuthRefresh: true,
            });
            applyUser(res.data);
        } catch (error) {
            const status = error?.response?.status;

            if (status === 401) {
                try {
                    await API.post("/users/auth/refresh/", null, {
                        skipAuthRefresh: true,
                    });

                    const retryRes = await API.get("/users/me/", {
                        skipAuthRefresh: true,
                    });

                    applyUser(retryRes.data);
                } catch (refreshError) {
                    clearUser();
                }
            } else {
                clearUser();
            }
        } finally {
            setAuthChecked(true);
        }
    }, [applyUser, clearUser]);


    const login = useCallback(async (usernameInput, password) => {
        setLoading(true);
        try {
            const res = await API.post(
                "/users/auth/login/",
                {
                    username: usernameInput,
                    password,
                },
                {
                    skipAuthRefresh: true,
                }
            );

            applyUser(res.data);
            setAuthChecked(true);

            return { success: true };
        } catch (error) {
            clearUser();
            setAuthChecked(true);

            return {
                success: false,
                error:
                    error.response?.data?.error ||
                    error.response?.data?.detail ||
                    "Ошибка авторизации",
            };
        } finally {
            setLoading(false);
        }
    }, [applyUser, clearUser]);

    const logout = useCallback(async () => {
        setLoading(true);
        try {
            await API.post("/users/auth/logout/", null, {
                skipAuthRefresh: true,
            });
        } catch (error) {
            // даже если сервер не ответил корректно, локально выходим
        } finally {
            clearUser();
            setAuthChecked(true);
            setLoading(false);
        }
    }, [clearUser]);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    useEffect(() => {
        const handleForcedLogout = () => {
            clearUser();
            setAuthChecked(true);
            setLoading(false);
        };

        window.addEventListener("auth:logout", handleForcedLogout);

        return () => {
            window.removeEventListener("auth:logout", handleForcedLogout);
        };
    }, [clearUser]);

    return (
        <AuthContext.Provider
            value={{
                token,
                username,
                role,
                loading,
                authChecked,
                isAuthenticated: !!token,
                login,
                logout,
                checkAuth,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};